import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import GroupIcon from "@material-ui/icons/Group";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SchoolIcon from "@material-ui/icons/School";
import managerClassApi from "api/managerClass";

export interface ClassOption {
  id: number | string;
  name: string;
}

export interface StudentOption {
  id: number | string;
  name: string;
  staffId?: string;
  classId?: number | string;
  className?: string;
}

export interface ManageExamStudentsModalProps {
  open: boolean;
  onClose: () => void;
  classList: ClassOption[];
  initialClassIds: (number | string)[];
  initialUserIds: (number | string)[];
  onSave: (selectedClassIds: (number | string)[], selectedUserIds: (number | string)[]) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    borderRadius: 16,
    padding: 0,
    [theme.breakpoints.down("sm") || "@media (max-width:960px)"]: {
      margin: "0 !important",
      width: "100% !important",
      height: "100% !important",
      maxHeight: "100% !important",
      maxWidth: "100% !important",
      borderRadius: "0 !important",
    },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 20px 10px 20px",
    borderBottom: "1px solid #E2E8F0",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
    },
  },
  dialogActions: {
    padding: "12px 20px",
    borderTop: "1px solid #E2E8F0",
    [theme.breakpoints.down("sm")]: {
      padding: "8px 12px",
    },
  },
  titleText: {
    fontWeight: 800,
    fontSize: "1.15rem",
    color: "#0F172A",
    display: "flex",
    alignItems: "center",
    gap: 8,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.9rem",
    },
  },
  dialogContent: {
    padding: "16px 20px !important",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: "8px !important",
      height: "calc(100vh - 100px)",
    },
  },
  gridContainer: {
    height: "100%",
    flex: 1,
  },
  columnGridItem: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    [theme.breakpoints.down("sm")]: {
      height: "50% !important",
      maxHeight: "50% !important",
      overflow: "hidden",
    },
  },
  columnBox: {
    height: 520,
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      height: "100%",
      flex: 1,
      overflow: "hidden",
    },
  },
  columnHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 36,
    height: 36,
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: "1px solid #CBD5E1",
    [theme.breakpoints.down("sm")]: {
      minHeight: 32,
      height: 32,
      marginBottom: 6,
      paddingBottom: 4,
    },
  },
  columnTitle: {
    fontWeight: 800,
    fontSize: "0.95rem",
    color: "#0F172A",
    display: "flex",
    alignItems: "center",
    gap: 6,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.825rem",
    },
  },
  classListContainer: {
    overflowY: "auto",
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    border: "1px solid #E2E8F0",
  },
  classListItem: {
    borderRadius: 6,
    margin: "3px 6px",
    width: "auto",
    "&:hover": {
      backgroundColor: "#F1F5F9",
    },
    [theme.breakpoints.down("sm")]: {
      paddingTop: 4,
      paddingBottom: 4,
    },
  },
  classListItemActive: {
    backgroundColor: "#E0F2FE !important",
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    border: "1px solid #E2E8F0",
  },
  tableHeaderCell: {
    backgroundColor: "#F1F5F9",
    fontWeight: 700,
    color: "#334155",
    fontSize: "0.825rem",
    padding: "10px 12px",
    [theme.breakpoints.down("sm")]: {
      padding: "6px 6px",
      fontSize: "0.75rem",
    },
  },
  tableBodyCell: {
    fontSize: "0.85rem",
    padding: "8px 12px",
    [theme.breakpoints.down("sm")]: {
      padding: "5px 6px",
      fontSize: "0.78rem",
    },
  },
  searchField: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    marginBottom: 10,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: 6,
    },
  },
  statChip: {
    fontWeight: 700,
    fontSize: "0.75rem",
    borderRadius: 6,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.68rem",
      height: 22,
    },
  },
}));

const getProp = (obj: any, ...keys: string[]) => {
  if (!obj || typeof obj !== "object") return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  const allObjKeys = Object.keys(obj);
  for (const k of keys) {
    const target = k.toLowerCase().replace(/_/g, "");
    const foundKey = allObjKeys.find((x) => x.toLowerCase().replace(/_/g, "") === target);
    if (foundKey && obj[foundKey] !== undefined && obj[foundKey] !== null) {
      return obj[foundKey];
    }
  }
  return undefined;
};

export default function ManageExamStudentsModal({
  open,
  onClose,
  classList,
  initialClassIds,
  initialUserIds,
  onSave,
}: ManageExamStudentsModalProps) {
  const classes = useStyles();
  const [selectedClassIds, setSelectedClassIds] = useState<(number | string)[]>([]);
  const [activeClassIdFilter, setActiveClassIdFilter] = useState<number | string | "ALL">("ALL");
  const [classStudentsMap, setClassStudentsMap] = useState<Record<string, StudentOption[]>>({});
  const [tempSelectedUserIds, setTempSelectedUserIds] = useState<(number | string)[]>([]);
  const [classSearchQuery, setClassSearchQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingClassId, setLoadingClassId] = useState<string | null>(null);

  // Map class names dynamically from classList prop
  const classNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (classList || []).forEach((c: any) => {
      const cId = getProp(c, "id", "classId", "classID", "value", "key");
      const cName = getProp(c, "name", "className", "title", "label");
      if (cId !== undefined && cId !== null) {
        map.set(String(cId), String(cName || ""));
      }
    });
    return map;
  }, [classList]);

  // Sync state & load ALL students with class info in 1 single API call when modal opens!
  useEffect(() => {
    if (open) {
      const clsIds = initialClassIds || [];
      setSelectedClassIds(clsIds);
      setTempSelectedUserIds(initialUserIds || []);
      setActiveClassIdFilter("ALL");
      setClassSearchQuery("");
      setSearchQuery("");

      (async () => {
        setLoadingClassId("ALL");
        try {
          const res: any = await managerClassApi.getAllStudentsWithClasses();
          const list = res.students || res.data?.students || res.data || [];
          const students: StudentOption[] = list.map((u: any) => {
            const cId = getProp(u, "classId", "class_id", "classID", "class");
            const sId = getProp(u, "staffId", "staff_id", "staffID", "id");
            const sName = getProp(u, "name", "userName", "fullName") || "";
            const uId = getProp(u, "id", "userId", "userID");
            const cName =
              getProp(u, "className", "class_name") ||
              (cId !== undefined ? classNameMap.get(String(cId)) : undefined) ||
              "";
            return {
              id: uId,
              name: sName,
              staffId: sId,
              classId: cId,
              className: cName,
            };
          });

          // Group students by classId for instant O(1) tab filtering
          const map: Record<string, StudentOption[]> = {};
          students.forEach((st) => {
            const cKey = String(st.classId);
            if (!map[cKey]) map[cKey] = [];
            map[cKey].push(st);
          });
          setClassStudentsMap(map);

          // If initialUserIds is empty but initialClassIds has values, auto-select all students of initial classes
          if ((!initialUserIds || initialUserIds.length === 0) && clsIds.length > 0) {
            const initialStudentSet = new Set<(number | string)>();
            clsIds.forEach((cId) => {
              const cStudents = map[String(cId)] || [];
              cStudents.forEach((s) => initialStudentSet.add(s.id));
            });
            setTempSelectedUserIds(Array.from(initialStudentSet));
          }
        } catch (error) {
          console.error("Failed to fetch all students with classes", error);
        } finally {
          setLoadingClassId(null);
        }
      })();
    }
  }, [open, initialClassIds, initialUserIds]);

  // All students loaded across classes
  const allLoadedStudents = useMemo(() => {
    const map = new Map<string, StudentOption>();
    Object.values(classStudentsMap).forEach((list) => {
      list.forEach((st) => map.set(String(st.id), st));
    });
    return Array.from(map.values());
  }, [classStudentsMap]);

  // Convert selected user IDs to Set for O(1) lookup
  const selectedUserSet = useMemo(
    () => new Set(tempSelectedUserIds.map((id) => String(id))),
    [tempSelectedUserIds]
  );

  // Overall counts across entire modal (all classes)
  const overallSelectedCount = tempSelectedUserIds.length;
  const overallTotalCount = allLoadedStudents.length;

  // Calculate total & selected count for "Tất cả các lớp"
  const totalAllStudents = allLoadedStudents.length;
  const selectedAllStudents = useMemo(
    () => allLoadedStudents.filter((s) => selectedUserSet.has(String(s.id))).length,
    [allLoadedStudents, selectedUserSet]
  );
  const isAllClassesSelected = totalAllStudents > 0 && selectedAllStudents === totalAllStudents;
  const isAllClassesIndeterminate = selectedAllStudents > 0 && selectedAllStudents < totalAllStudents;

  // Toggle single class selection -> Check class automatically checks/tickets ALL students in that class!
  const handleToggleClass = (cId: number | string) => {
    const key = Number(cId);
    const classStudents = classStudentsMap[String(cId)] || [];
    const classStudentIds = classStudents.map((s) => s.id);
    const classStudentIdSet = new Set(classStudentIds.map((id) => String(id)));

    const selectedInClass = classStudents.filter((s) => selectedUserSet.has(String(s.id))).length;
    const isFullySelected = classStudents.length > 0 && selectedInClass === classStudents.length;

    let updatedClassIds: (number | string)[];
    let updatedUserIds: (number | string)[];

    if (isFullySelected) {
      // If fully checked -> Uncheck class & remove all students of this class
      updatedClassIds = selectedClassIds.filter((x) => Number(x) !== key);
      updatedUserIds = tempSelectedUserIds.filter((id) => !classStudentIdSet.has(String(id)));
    } else {
      // If unchecked or partially checked -> Ticket ALL students in this class
      const userSet = new Set(tempSelectedUserIds.map((id) => String(id)));
      const existingUserMap = new Map(tempSelectedUserIds.map((id) => [String(id), id]));
      classStudents.forEach((s) => {
        userSet.add(String(s.id));
        existingUserMap.set(String(s.id), s.id);
      });
      updatedUserIds = Array.from(userSet).map((idStr) => existingUserMap.get(idStr)!);
      updatedClassIds = selectedClassIds.map((x) => Number(x)).includes(key)
        ? selectedClassIds
        : [...selectedClassIds, cId];
    }

    setSelectedClassIds(updatedClassIds);
    setTempSelectedUserIds(updatedUserIds);
  };

  const handleSelectAllClasses = () => {
    if (isAllClassesSelected) {
      setSelectedClassIds([]);
      setTempSelectedUserIds([]);
    } else {
      setSelectedClassIds(classList.map((c) => c.id));
      setTempSelectedUserIds(allLoadedStudents.map((s) => s.id));
    }
  };

  // Filter class list by search query
  const filteredClassList = useMemo(() => {
    if (!classSearchQuery.trim()) return classList;
    const q = classSearchQuery.toLowerCase().trim();
    return classList.filter((c) => c.name.toLowerCase().includes(q));
  }, [classList, classSearchQuery]);

  // Filter students by active class filter & search query
  const filteredStudents = useMemo(() => {
    let list: StudentOption[] = [];
    if (activeClassIdFilter === "ALL") {
      list = allLoadedStudents;
    } else {
      list = classStudentsMap[String(activeClassIdFilter)] || [];
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          (s.staffId && String(s.staffId).toLowerCase().includes(q)) ||
          (s.className && s.className.toLowerCase().includes(q))
      );
    }
    return list;
  }, [activeClassIdFilter, allLoadedStudents, classStudentsMap, searchQuery]);

  const totalCount = filteredStudents.length;
  const selectedCount = useMemo(
    () => filteredStudents.filter((s) => selectedUserSet.has(String(s.id))).length,
    [filteredStudents, selectedUserSet]
  );

  // Toggle single student
  const handleToggleStudent = (student: StudentOption) => {
    const key = String(student.id);
    const isSelected = selectedUserSet.has(key);
    let updatedUserIds: (number | string)[];

    if (isSelected) {
      updatedUserIds = tempSelectedUserIds.filter((x) => String(x) !== key);
    } else {
      updatedUserIds = [...tempSelectedUserIds, student.id];
    }
    setTempSelectedUserIds(updatedUserIds);

    // If selecting a student, ensure student's class is checked in selectedClassIds
    if (!isSelected && student.classId) {
      const cIdNum = Number(student.classId);
      if (!selectedClassIds.map((x) => Number(x)).includes(cIdNum)) {
        setSelectedClassIds([...selectedClassIds, student.classId]);
      }
    }
  };

  // Select/Deselect all filtered students in table
  const isAllFilteredSelected =
    filteredStudents.length > 0 &&
    filteredStudents.every((s) => selectedUserSet.has(String(s.id)));

  const handleToggleAllFilteredInTable = () => {
    if (isAllFilteredSelected) {
      const filteredIdSet = new Set(filteredStudents.map((s) => String(s.id)));
      setTempSelectedUserIds(
        tempSelectedUserIds.filter((id) => !filteredIdSet.has(String(id)))
      );
    } else {
      const filteredIds = filteredStudents.map((s) => s.id);
      const newSet = new Set([...tempSelectedUserIds, ...filteredIds]);
      setTempSelectedUserIds(Array.from(newSet));

      // Also ensure all class IDs of filtered students are checked
      const classIdsToAdd = filteredStudents.map((s) => s.classId).filter(Boolean);
      const currentClassSet = new Set(selectedClassIds.map((x) => Number(x)));
      const updatedClassIds = [...selectedClassIds];
      classIdsToAdd.forEach((cId) => {
        if (cId && !currentClassSet.has(Number(cId))) {
          currentClassSet.add(Number(cId));
          updatedClassIds.push(cId);
        }
      });
      setSelectedClassIds(updatedClassIds);
    }
  };

  const handleConfirm = () => {
    onSave(selectedClassIds, tempSelectedUserIds);
    onClose();
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          <GroupIcon style={{ color: "#0284C7" }} /> DANH SÁCH LỚP HỌC & HỌC VIÊN DỰ THI
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2} className={classes.gridContainer}>
          {/* CỘT BÊN TRÁI: Danh Sách Lớp (List Checkbox & Click to View) */}
          <Grid item xs={12} md={4} className={classes.columnGridItem}>
            <Box className={classes.columnBox}>
              <Box className={classes.columnHeader}>
                <Typography className={classes.columnTitle}>
                  <SchoolIcon style={{ color: "#0284C7", fontSize: 20 }} /> Danh Sách Lớp ({selectedClassIds.length}/{classList.length})
                </Typography>
                <Button
                  size="small"
                  onClick={handleSelectAllClasses}
                  style={{ fontSize: "0.75rem", fontWeight: 700, color: "#0284C7", textTransform: "none" }}
                >
                  {isAllClassesSelected ? "Bỏ tất cả" : "Chọn tất cả"}
                </Button>
              </Box>

              {/* Class Search Field */}
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Tìm tên lớp..."
                value={classSearchQuery}
                onChange={(e) => setClassSearchQuery(e.target.value)}
                className={classes.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "#94A3B8" }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Class Selection Checkbox List */}
              <Box className={classes.classListContainer}>
                <List dense disablePadding>
                  {!classSearchQuery && (
                    <ListItem
                      button
                      className={`${classes.classListItem} ${
                        activeClassIdFilter === "ALL" ? classes.classListItemActive : ""
                      }`}
                      onClick={() => setActiveClassIdFilter("ALL")}
                    >
                      <ListItemIcon style={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={isAllClassesSelected}
                          indeterminate={isAllClassesIndeterminate}
                          onChange={handleSelectAllClasses}
                          color="primary"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={<b>Tất cả các lớp</b>}
                        secondary={`${classList.length} lớp học`}
                      />
                    </ListItem>
                  )}

                  {filteredClassList.map((cls) => {
                    const classStudents = classStudentsMap[String(cls.id)] || [];
                    const totalInClass = classStudents.length;
                    const selectedInClass = classStudents.filter((s) =>
                      selectedUserSet.has(String(s.id))
                    ).length;

                    const isChecked = totalInClass > 0 && selectedInClass === totalInClass;
                    const isIndeterminate = selectedInClass > 0 && selectedInClass < totalInClass;
                    const isActiveFilter = Number(activeClassIdFilter) === Number(cls.id);

                    return (
                      <ListItem
                        key={cls.id}
                        button
                        className={`${classes.classListItem} ${
                          isActiveFilter ? classes.classListItemActive : ""
                        }`}
                        onClick={() => setActiveClassIdFilter(cls.id)}
                      >
                        <ListItemIcon style={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={isChecked}
                            indeterminate={isIndeterminate}
                            onChange={() => handleToggleClass(cls.id)}
                            color="primary"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={cls.name}
                          secondary={
                            totalInClass > 0
                              ? `${selectedInClass}/${totalInClass} học viên`
                              : undefined
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Box>
          </Grid>

          {/* CỘT BÊN PHẢI: Danh Sách Học Viên (Table & Search) */}
          <Grid item xs={12} md={8} className={classes.columnGridItem}>
            <Box className={classes.columnBox}>
              <Box className={classes.columnHeader}>
                <Typography className={classes.columnTitle}>
                  <GroupIcon style={{ color: "#0284C7", fontSize: 20 }} /> Danh Sách Học Viên{" "}
                  {activeClassIdFilter !== "ALL" && (
                    <span style={{ color: "#0284C7", fontWeight: 600, fontSize: "0.85rem" }}>
                      ({classList.find((c) => Number(c.id) === Number(activeClassIdFilter))?.name})
                    </span>
                  )}
                </Typography>
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                  <Chip
                    size="small"
                    label={`Đang xem: ${selectedCount}/${totalCount}`}
                    className={classes.statChip}
                    style={{ backgroundColor: "#E2E8F0", color: "#334155" }}
                  />
                  <Chip
                    size="small"
                    icon={<CheckCircleIcon style={{ color: "#FFFFFF", fontSize: 14 }} />}
                    label={`Tổng đã chọn: ${overallSelectedCount}`}
                    className={classes.statChip}
                    style={{ backgroundColor: "#0284C7", color: "#FFFFFF" }}
                  />
                </Box>
              </Box>

              {/* Search Field */}
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Tìm theo Tên, Mã nhân viên..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={classes.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "#94A3B8" }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Student Table */}
              <TableContainer className={classes.tableContainer}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeaderCell} width="50" align="center">
                        <Checkbox
                          checked={isAllFilteredSelected}
                          onChange={handleToggleAllFilteredInTable}
                          color="primary"
                          size="small"
                          disabled={filteredStudents.length === 0}
                        />
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>Mã NV / ID</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Họ và Tên</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Lớp học</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingClassId ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" style={{ padding: 24, color: "#64748B" }}>
                          <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 8 }}>
                            <CircularProgress size={18} color="primary" />
                            <span>Đang tải danh sách học viên...</span>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : filteredStudents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" style={{ padding: 24, color: "#94A3B8" }}>
                          {activeClassIdFilter === "ALL"
                            ? "Vui lòng chọn hoặc bấm vào 1 Lớp học ở cột bên trái để hiển thị danh sách."
                            : "Không tìm thấy học viên nào trong lớp này."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredStudents.map((student) => {
                        const isSelected = selectedUserSet.has(String(student.id));
                        return (
                          <TableRow
                            key={student.id}
                            hover
                            selected={isSelected}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleToggleStudent(student)}
                          >
                            <TableCell align="center" className={classes.tableBodyCell}>
                              <Checkbox
                                checked={isSelected}
                                color="primary"
                                size="small"
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => handleToggleStudent(student)}
                              />
                            </TableCell>
                            <TableCell className={classes.tableBodyCell}>
                              <b>{student.staffId || student.id}</b>
                            </TableCell>
                            <TableCell className={classes.tableBodyCell}>
                              <b>{student.name}</b>
                            </TableCell>
                            <TableCell className={classes.tableBodyCell}>
                              {(() => {
                                const rawClassId = getProp(student, "classId", "class_id", "classID");
                                const strClassId = rawClassId !== undefined && rawClassId !== null ? String(rawClassId) : "";
                                
                                // 1. Check in classNameMap
                                if (strClassId && classNameMap.has(strClassId)) {
                                  return classNameMap.get(strClassId);
                                }
                                
                                // 2. Check directly in student object
                                const directName = getProp(student, "className", "class_name");
                                if (directName) return directName;

                                // 3. Check directly in classList array
                                if (strClassId) {
                                  const matchedClass = (classList || []).find(
                                    (c: any) => String(getProp(c, "id", "classId", "classID", "value", "key")) === strClassId
                                  );
                                  if (matchedClass) {
                                    return getProp(matchedClass, "name", "className", "title", "label") || "-";
                                  }
                                }
                                return "-";
                              })()}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} style={{ color: "#64748B", fontWeight: 600 }}>
          Hủy bỏ
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          style={{ backgroundColor: "#0284C7", color: "#FFFFFF", fontWeight: 700, borderRadius: 8, padding: "6px 24px" }}
        >
          Xác nhận ({overallSelectedCount} Học viên)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
